// One document of the files list on a tablet or desktop (≥ md): a compact row
//   [checkbox] [status pill + title / meta line / signer chips] [actions]
// Related documents open below it, indented, as the same row without a checkbox.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { Download, Eye } from "lucide-react";
import FileRowMenu from "./file-row-menu";
import {
  DISABLED_LINK,
  FileTitle,
  ICON_BUTTON,
  MetaLine,
  RelatedToggle,
  SignerChips,
  StatusPillBadge,
  UploadSignedCopyInput,
} from "./file-row-parts";
import type { FileItem } from "./file-item";
import useFileRow, { type FilesListContext } from "./use-file-row";

type Props = {
  item: FileItem;
  ctx: FilesListContext;
  isChild?: boolean;
};

export default function FileRow({ item, ctx, isChild = false }: Props) {
  const { t } = useTranslation();
  const row = useFileRow(item, ctx, isChild);
  const [relatedOpen, setRelatedOpen] = useState(false);
  const relatedListId = `related-files-${item.id}`;

  return (
    <div className="border-b border-zinc-200 last:border-b-0">
      <div
        className={clsx(
          "grid items-start gap-3 px-1 py-2.5",
          row.isSelectable
            ? "grid-cols-[auto_minmax(0,1fr)_auto]"
            : "grid-cols-[minmax(0,1fr)_auto]",
          row.isSelected && "rounded-md bg-indigo-50"
        )}
      >
        {row.isSelectable && (
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={row.isSelected}
            onChange={row.toggleSelected}
            aria-label={`${t("select")}: ${row.title}`}
          />
        )}
        <div className="min-w-0">
          <p className="text-sm leading-snug">
            {row.status && (
              <>
                <StatusPillBadge status={row.status} />{" "}
              </>
            )}
            <FileTitle title={row.title} url={row.documentPageUrl} />
          </p>
          <MetaLine
            item={item}
            number={row.number}
            categoryTitle={row.categoryTitle}
            extra={
              row.related.length > 0 && (
                <RelatedToggle
                  count={row.related.length}
                  expanded={relatedOpen}
                  onToggle={() => setRelatedOpen((open) => !open)}
                  controls={relatedListId}
                />
              )
            }
          />
          {row.signers.length > 0 && (
            <SignerChips
              signers={row.signers}
              onRemind={row.remind}
              remindingKey={row.remindingKey}
            />
          )}
        </div>
        <div className="flex items-center gap-1">
          {row.primary && (
            <button
              type="button"
              className="mr-1 whitespace-nowrap rounded-md bg-indigo-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
              onClick={row.primary.run}
            >
              {row.primary.label}
            </button>
          )}
          <button
            type="button"
            className={clsx(ICON_BUTTON, "disabled:opacity-40 disabled:hover:bg-white")}
            disabled={!row.canView}
            onClick={row.view}
            aria-label={t("view")}
            title={t("view")}
          >
            <Eye className="h-4 w-4" />
          </button>
          <a
            href={row.downloadUrl || undefined}
            target="_blank"
            rel="noreferrer"
            className={clsx(ICON_BUTTON, !row.downloadUrl && DISABLED_LINK)}
            aria-disabled={!row.downloadUrl}
            aria-label={t("download")}
            title={t("download")}
          >
            <Download className="h-4 w-4" />
          </a>
          <FileRowMenu items={row.menuItems} ariaLabel={t("more")} />
          {row.signedCopyUpload && (
            <UploadSignedCopyInput
              ref={row.signedCopyUpload.inputRef}
              onFile={row.signedCopyUpload.onFile}
            />
          )}
        </div>
      </div>
      {row.related.length > 0 && (
        // Always in the DOM, so the toggle's aria-controls points at it;
        // the rows themselves mount only while it is open.
        <div
          id={relatedListId}
          hidden={!relatedOpen}
          className="mb-2 ml-7 border-l-2 border-zinc-200 pl-3"
        >
          {relatedOpen &&
            row.related.map((child) => (
              <FileRow key={child.id} item={child} ctx={ctx} isChild />
            ))}
        </div>
      )}
    </div>
  );
}
